import { createRoute, Outlet, redirect, useLocation } from "@tanstack/solid-router";
import { rootRoute } from "../__root";
import { api } from "@/lib/api";
import { sessionActor } from "@/lib/session-machine";
import { queryClient } from "@/lib/query-client";
import { appState, PageTransition } from "@fleet/ui";
import { cloneSession, isValidSession, queryKeys } from "@malla/shared";
import { SESSION_CHECK_TIMEOUT_MS } from "@/lib/constants";

/**
 * Malla authentication wrapper.
 *
 * NOTE (#13 — des-clon): Malla deliberately does NOT use the shared
 * `AuthLayout` (the split brand-panel + glass `AuthCard` shell every clone
 * inherited). Instead each screen owns its own `AuthKiosk` — a single wide
 * service console (brand bar + hero banner + flat large-touch form on the
 * ambient background), sized for a counter tablet. So this route is a thin
 * wrapper: it only owns the "already authenticated → redirect" gate and the
 * page transition. The background, brand band and scroll container live in
 * `AuthKiosk`, rendered per-screen.
 */
/**
 * Session request from the `_auth` guard.
 *
 * Deliberately at MODULE scope: defined inside `beforeLoad`,
 * `unicorn/consistent-function-scoping` flags it as an error in `pnpm lint`
 * while `tsc --noEmit` remains green.
 */
const fetchSession = async () =>
  api.GET("/api/auth/get-session", { params: { query: { jwt: true } } });

type FetchResult = Awaited<ReturnType<typeof fetchSession>>;

/**
 * Synchronizes the session machine and cache with the server response.
 *
 * It ALWAYS runs when the response arrives, even if the timeout has already
 * expired and the login is displayed: the request is not canceled. If there
 * turned out to be a session, `BootManager` takes the user out of `/login`—a
 * flicker is paid, never a blank screen.
 */
const syncReply = (reply: FetchResult) => {
  const { data: replyData, error: replyError } = reply;
  // Synchronize the machine and the cache immediately.
  // This prevents the BootManager from seeing a "pending" or stale session
  // during the route transition, which causes redirection loops.
  sessionActor.send({
    type: "SERVER_REPLY",
    session: isValidSession(replyData) ? cloneSession(replyData) : null,
    error: replyError ?? null,
  });
  queryClient.setQueryData(queryKeys.auth.session(), replyData ?? null);
  return reply;
};

function AuthLayoutRoute() {
  const location = useLocation();
  appState.markAsReady();

  return (
    <PageTransition routeKey={location().pathname}>
      <Outlet />
    </PageTransition>
  );
}

/**
 * Guard of the _auth layout: an authenticated user is sent to the app.
 */
export const authGuard = async ({ search }: { search: { mode?: string; redirect?: string } }) => {
  // Multi-account: `?mode=add` opens /login to ADD another account while the
  // current one stays signed in (Gmail style). The "already authenticated →
  // go home" gate must be skipped here; otherwise the switcher's "Add account"
  // would bounce straight back to the app and a 2nd login could never be added.
  const isAddingAccount = search.mode === "add";
  if (isAddingAccount) {
    return;
  }

  // Fast path: check machine state before hitting the network.
  const snapshot = sessionActor.getSnapshot();
  if (!snapshot.matches("priming") && (snapshot.context.session || snapshot.context.isVerified)) {
    if (snapshot.context.session) {
      // oxlint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: search.redirect ?? "/" });
    }
    return;
  }

  // Network check — only if machine has no session yet.
  // Use api.GET (not fetchApi) so we can inspect the status without throwing.
  //
  // P23/CC-60 · I1-bis — MEASURED WITH A BROWSER: this `await` must NOT be
  // unlimited. With `get-session` STALLED (held: neither a 401 nor a 5xx, which
  // resolve quickly and give a FALSE GREEN), `beforeLoad` did not resolve,
  // TanStack did not mount the route, and `/login` remained at
  // `#root.childElementCount === 0` after 3, 6, 10, 15, and 20 s—the ENTIRE
  // app was blank. The sibling guard in `_app` already had a timeout, which
  // is why `/` did render: phase 1 considered I1 closed by measuring the healthy route.
  //
  // CAREFUL when measuring: `document.body.innerText` does NOT work as an
  // anchor in this repo—`#app-loader` in `index.html` paints its mark even if
  // the app does not mount, leaving `innerText.length === 5`. The anchor is
  // `#root.childElementCount`.
  //
  // The request is NOT canceled: `syncReply` stays attached and updates the
  // machine and cache when it arrives. If it carried a session, `BootManager`
  // redirects.
  const inFlight = fetchSession()
    .then(syncReply)
    // Network error — let user try to log in anyway.
    .catch(() => null);

  const timedOut = new Promise<null>((resolve) => {
    setTimeout(() => {
      resolve(null);
    }, SESSION_CHECK_TIMEOUT_MS);
  });

  const result = await Promise.race([inFlight, timedOut]);

  if (!result) {
    // Timeout expired or network error: the login is DISPLAYED. The late
    // response remains attached above, and BootManager redirects if it has a session.
    return;
  }

  const { data } = result;

  // Redirect is thrown OUTSIDE the try/catch so it propagates to TanStack Router.
  // Better Auth returns HTTP 200 with a null body for unauthenticated requests,
  // but the OpenAPI schema types Session as non-null. Cast to match runtime reality.
  // A late reply for the session that LOGOUT revoked must not bounce /login back
  // to the app: the machine already ignores it, and so does this check.
  const sessionData = data as { user?: unknown; session?: { id?: string } } | null | undefined;
  const revokedIds = sessionActor.getSnapshot().context.revokedSessionIds;
  const isRevoked =
    sessionData?.session?.id !== undefined && revokedIds.includes(sessionData.session.id);
  if (sessionData?.user && !isRevoked) {
    // oxlint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: (search as { redirect?: string }).redirect ?? "/" });
  }
  // 401/403, network error, or null session — not authenticated, show login page.
};

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_auth",
  beforeLoad: authGuard,
  component: AuthLayoutRoute,
});
